/**
 * @module  experience/browse-services
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // Need to support all data types
import { Injectable } from '@angular/core';

// Services
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

// Interfaces
import { APIError } from '@support/api/common/error.interface';
import { ConfigurableServiceAEMData } from '@core/aem/interfaces/shop/browse-services.interface';
import {
  CompleteServicesInterface,
  ServiceBladeInterface,
  BladeEntries,
  ServiceCategory,
} from '@experience/shop/shared-browse/interfaces/service-blade.interface';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import {
  ServicePostRequestInterface,
  ServiceSelectInterface,
  ServiceRequestObjInterface,
} from '@experience/shop/browse-services/interfaces/services.interface';
import { Cart } from '@support/api/proxies/responses/api-cart-response.interface';

// Constants
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { PayLoadServiceRequest } from '@support/api/proxies/requests/api-2102.interface';
import { ORDER_TYPE } from '@experience/shop/shop.constants';
import { MANAGE_PROFILE } from '@support/shared/constants/app-routes.constants';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpResponse, HttpHeaders } from '@angular/common/http';
@Injectable({
  providedIn: 'root',
})
export class BrowseServicesService {
  constructor(
    private apiGatewayService: ApiGatewayService,
    private contentProviderService: ContentProviderService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private cartSupportService: CartSupportService,
    private objectService: ObjectService
  ) {}

  /**
   * Retrieves AEM content for the browse-services components
   * @return Observable<BrowseServiceContent>
   */
  public getBrowseServicesAEMContent(): Observable<ConfigurableServiceAEMData> {
    return this.contentProviderService.getContentData(null, 'postpaid').pipe(
      map((response: ConfigurableServiceAEMData) => {
        return response;
      })
    );
  }

  /**
   * @name getServiceData
   * @param isCPS (Optional) Boolean flag to identify if it is a CPS flow
   * @returns Observable<CompleteServicesInterface>
   * @description Method to return services
   */
  public getServiceData(isCPS?: boolean): Observable<CompleteServicesInterface> {
    const payload = this.getServiceRequestPayload(isCPS);
    const queryParams = payload.queryParams;
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId()
        ? this.stateStoreUtilsService.getCartId()
        : 'CARTID',
      lineId: isCPS
        ? this.stateStoreUtilsService.getLineId()
        : this.stateStoreUtilsService.getFocusedTileId(),
    };
    const customerId = this.stateStoreUtilsService.getCustomerId();
    const payLoadServiceRequest: PayLoadServiceRequest = {
      lineId: isCPS
        ? this.stateStoreUtilsService.getLineId()
        : this.stateStoreUtilsService.getFocusedTileId(),
      customerId: this.stateStoreUtilsService.getBscsCustomerID(),
      accountId: this.stateStoreUtilsService.getAddFan()
        ? ''
        : this.stateStoreUtilsService.getAccountId(),
      dealerCode: this.stateStoreUtilsService.getDealerCode()
        ? this.stateStoreUtilsService.getDealerCode().toString()
        : '',
      transactionTypes: isCPS
        ? [ORDER_TYPE.MANAGERATEPLAN]
        : customerId
          ? [ORDER_TYPE.ADDALINE]
          : [ORDER_TYPE.ACTIVATION],
    };
    const headers = new HttpHeaders().append(
      'billingType',
      this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus()
    );
    return this.apiGatewayService
      .postRequest(
        API_MAP.API_2102_retrieveservices,
        payLoadServiceRequest,
        headers,
        queryParams,
        pathParams
      )
      .pipe(
        map((response: HttpResponse<CompleteServicesInterface>) => {
          const data = response.body;
          this.stateStoreUtilsService.setInitialServiceLoadData(data);
          return data;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * @name postServiceSelection
   * @param familyId family id of the service that is selected/unselected
   * @param itemId Offer id of the service that is selected/unselected
   * @param isCPS (Optional) Boolean flag to identify if it is CPS flow
   * @returns Observable<ServicePostRequestInterface>
   * @description Method to return updated services based on selection
   */
  public postServiceSelection(
    familyId: string,
    itemId: string,
    isCPS?: boolean,
    isUpgrade?: boolean
  ): Observable<ServicePostRequestInterface> {
    const payload = this.getServiceSelectPayload(familyId, itemId, isCPS, isUpgrade);
    const pathParams = payload.pathParams;
    const queryParams = payload.queryParams;
    const requestBody = payload.requestObject;
    return this.apiGatewayService
      .putRequest(API_MAP.API_2113_postServiceSelection, requestBody, null, queryParams, pathParams)
      .pipe(
        map((response: HttpResponse<Object>) => response.body),
        map((response: CompleteServicesInterface) => {
          return {
            response: response,
            selectionChange: this.compareServiceObj(response),
          };
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * @name getServiceRequestPayload
   * @param isCPS (Optional) Boolean flag to identify if it is a CPS flow
   * @returns ServiceRequestObjInterface
   * @description Method to create API payload
   */
  public getServiceRequestPayload(isCPS?: boolean): ServiceRequestObjInterface {
    // TODO determine logic for default information, pending flow integration
    const lineId = isCPS
      ? this.stateStoreUtilsService.getLineId()
      : this.stateStoreUtilsService.getFocusedTileId();
    const customerConfigData: BrowseEntry = this.stateStoreUtilsService.getCustomerConfigData();
    const zip =
      this.objectService.get(this.stateStoreUtilsService.getBillingAddress(), 'postalCode') ||
      this.objectService.get(customerConfigData, 'zip');
    return {
      requestObject: {
        cartId: this.stateStoreUtilsService.getCartId(),
        lineId: lineId,
      },
      queryParams: {
        nested: 'TRUE',
        zipcode: zip,
      },
    };
  }

  /**
   * @name getServiceSelectPayload
   * @param familyId Family Id of the service which is selected/unselected
   * @param itemId Offer code of the service which is selected/unselected
   * @param isCPS (Optional) Boolean flag to identify if it is a CPS flow
   * @returns ServiceSelectInterface
   * @description Method to create API payload
   */
  public getServiceSelectPayload(
    familyId: string,
    itemId: string,
    isCPS?: boolean,
    isUpgrade?: boolean
  ): ServiceSelectInterface {
    let lineId: string;
    if (isCPS) {
      lineId = this.stateStoreUtilsService.getLineId();
    } else {
      lineId = !isUpgrade
        ? this.stateStoreUtilsService.getFocusedTileId()
        : this.objectService.get(
            this.stateStoreUtilsService.getFocusedTileLineNonLineItem(),
            'plansFromCart.id'
          );
    }
    let zip: string;
    if (
      isCPS &&
      this.stateStoreUtilsService.getCPSEntryPoint() === MANAGE_PROFILE.CHANGE_BILLING_ADDRESS
    ) {
      zip = this.objectService.get(this.stateStoreUtilsService.getBillingAddress(), 'postalCode');
    } else {
      const customerConfigData: BrowseEntry = this.stateStoreUtilsService.getCustomerConfigData();
      zip =
        this.objectService.get(this.stateStoreUtilsService.getBillingAddress(), 'postalCode') ||
        this.objectService.get(customerConfigData, 'zip');
    }
    return {
      requestObject: {
        id: familyId,
        offerId: itemId,
        customerId: this.stateStoreUtilsService.getBscsCustomerID() || '',
      },
      pathParams: {
        cartId: this.stateStoreUtilsService.getCartId() || 'CARTID',
        lineId: lineId,
      },
      queryParams: {
        nested: 'TRUE',
        zipcode: zip,
      },
    };
  }

  /**
   * Gets saved user input data
   * @returns BrowseEntry from stateStoreService or default data
   */
  public getUserInputsConfigData(): BrowseEntry {
    return this.stateStoreUtilsService.getCustomerConfigData();
  }

  /**
   * Compares the post service selection data with the initial service data,
   * @returns boolean truthy if change occurs
   */
  public compareServiceObj(updatedData: CompleteServicesInterface): boolean {
    const previousData = this.stateStoreUtilsService.getInitialServiceLoadData();
    return JSON.stringify(previousData) !== JSON.stringify(updatedData);
  }

  /**
   * Service Bundle For Selected Services
   * update cart object in statestore
   * @returns Observable<Cart>
   */
  public postServiceBundle(): Observable<Cart> {
    const lineId = this.stateStoreUtilsService.getFocusedTileId();
    const selectedServicedata: ServicePostRequestInterface =
      this.stateStoreUtilsService.getServiceData();
    const serviceBundleId: string =
      selectedServicedata && selectedServicedata.response && selectedServicedata.response.id
        ? selectedServicedata.response.id
        : '';
    return this.cartSupportService.postServiceBundle(serviceBundleId, lineId);
  }

  /**
   * Method to get list of services Id for the services that are selected
   * @param selectedServices : Current service structure
   * @returns : string[]
   */
  public getSelectedServicesId(selectedServices: ServicePostRequestInterface): string[] {
    let selectedServicesId: string[] = [];
    const categories = this.objectService.get(selectedServices, 'response.category');
    if (categories && Array.isArray(categories)) {
      categories.forEach((category: ServiceCategory) => {
        category.family.forEach((family: ServiceBladeInterface) => {
          const services: string[] = family.details
            .filter((service: BladeEntries) => service.selected === true)
            .map((service: BladeEntries) => service.id);
          selectedServicesId = selectedServicesId.concat(services);
        });
      });
    }
    return selectedServicesId;
  }
}
